import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import * as action from '../store/POTemplate.action';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import DragDropSort from '../../../../Components/DragDropSort/DragDropSort';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { POTemplateDndProvider, usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import '../POTemplate.css';

function PurchaseOrderTemplateContent() {
  const reduxDispatch = useDispatch();
  const { t } = useTranslation('purchase-order');
  const history = useHistory();
  const { detailState, dispatch, setValidationError, clearAllValidationErrors, paramId } = usePOTemplateDnd();
  const [showRearrangeModal, setShowRearrangeModal] = useState(false);

  const goBackToListingPage = () => {
    dispatch({ type: 'RESET_POTEMPLATE_DETAILS' });
    history.push('/libraries/purchase-order-template');
  };

  const validateTemplate = () => {
    let hasErrors = false;
    clearAllValidationErrors();

    if (!detailState?.templateName?.trim()) {
      setValidationError('templateName', t('ADD_PO_TEMPLATE_PROVIDER.TEMPLATE_NAME_REQUIRED'));
      hasErrors = true;
    }

    const orderDetails = detailState?.orderDetails || [];
    orderDetails.forEach((order, index) => {
      if (!order?.packType) {
        setValidationError('packType', t('PO_TEMPLATE.VALIDATION.PACK_TYPE_REQUIRED'), index);
        hasErrors = true;
      }
    });
    return !hasErrors;
  };

  const handleSave = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('loggedInUserInfo') || '{}');
      const userId = userInfo?.userId;
      if (!userId) {
        toasterMsg(t('COMMON_LABEL.USER_NOT_FOUND'), 'danger');
        return;
      }
      if (!validateTemplate()) {
        toasterMsg(t('PO_TEMPLATE.VALIDATION.MISSING_REQUIRED_FIELDS'), 'danger');
        return;
      }
      const templateData = {
        _id: paramId || detailState._id || '',
        templateName: detailState.templateName?.trim(),
        purchaseOrderNumber: detailState.purchaseOrderNumber?.trim() || '',
        purchaseOrderDate: detailState.purchaseOrderDate || null,
        companyLogo: detailState.poImage || null,
        originalPOImage: detailState.originalPOImage || null,
        deleteS3Images: detailState.deleteS3Images || false,
        companyDetails: {
          companyName: detailState.companyDetails?.companyName?.trim() || '',
          companyNumber: detailState.companyDetails?.companyNumber || '',
          vatRegistrationNumber: detailState.companyDetails?.vatRegistrationNumber?.trim() || '',
          paymentTerms: detailState.companyDetails?.paymentTerms?.trim() || '',
          billingAddress: detailState.companyDetails?.billingAddress?.trim() || '',
          contactPersonName: detailState.companyDetails?.contactPersonName?.trim() || '',
          contactPersonEmail: detailState.companyDetails?.contactPersonEmail?.trim() || '',
          createdByName: detailState.companyDetails?.createdByName?.trim() || '',
          createdByEmail: detailState.companyDetails?.createdByEmail?.trim() || '',
          shippingAddress: detailState.companyDetails?.shippingAddress?.trim() || '',
        },
        supplierFactoryDetails: {
          supplierName: detailState.supplierFactoryDetails?.supplierName?.trim() || '',
          supplierCode: detailState.supplierFactoryDetails?.supplierCode?.trim() || '',
          supplierContactName: detailState.supplierFactoryDetails?.supplierContactName?.trim() || '',
          supplierContactEmail: detailState.supplierFactoryDetails?.supplierContactEmail?.trim() || '',
          supplierAddress: detailState.supplierFactoryDetails?.supplierAddress?.trim() || '',
          factoryName: detailState.supplierFactoryDetails?.factoryName?.trim() || '',
          factoryCode: detailState.supplierFactoryDetails?.factoryCode?.trim() || '',
          factoryContactName: detailState.supplierFactoryDetails?.factoryContactName?.trim() || '',
          factoryContactEmail: detailState.supplierFactoryDetails?.factoryContactEmail?.trim() || '',
          factoryAddress: detailState.supplierFactoryDetails?.factoryAddress?.trim() || '',
        },
        shippingDetails: {
          shippingMethod: detailState.shippingDetails?.shipmentMethod?.trim() || '',
          incoterms: detailState.shippingDetails?.incoterms?.trim() || '',
          shippingAndHandlingCosts: detailState.shippingDetails?.shippingCosts || 0,
          portOfLanding: detailState.shippingDetails?.portOfLanding?.trim() || '',
          portOfLading: detailState.shippingDetails?.portOfLoading?.trim() || '',
        },
        orderDetails:
          detailState.orderDetails?.map((order) => ({
            packType: order.packType?.value || order.packType,
            productNumber: order.productNo?.value || order.productNo,
            colourway: order.colourWay?.value || order.colourWay,
            size: order.size?.value || order.size,
            length: order.length?.value || order.length,
            ratio: order.ratio || 0,
            packs: order.packs || 0,
            quantity: order.quantity || 0,
            location: order.location?.trim() || '',
            unitPrice: order.unitPrice || 0,
            discountPercent: order.discount || 0,
            priceAfterDiscount: order.priceAfterDiscount || 0,
            total: order.total || 0,
            exFactoryDate: order.exFactoryDate || null,
            shipmentDate: order.shipmentDate || null,
          })) || [],
        grandTotal: {
          grandTotalUnits: detailState.grandTotal?.grandTotalUnits || 0,
          shippingCost: detailState.grandTotal?.shippingCosts || 0,
          grandTotalPriceAfterDiscount: detailState.grandTotal?.grandTotalPriceAfterDiscount || 0,
        },
        sections: detailState.sections || [],
        isActive: true,
        createdBy: userId,
        modifiedBy: userId,
      };

      const isCreating = !paramId && !detailState._id;
      const res = paramId ? await reduxDispatch(action.updatePOTemplate(templateData, t)) : await reduxDispatch(action.addPOTemplate(templateData, t));

      if (res?.code === 200) {
        if (isCreating && res?.data?._id) {
          const newTemplateId = res.data._id;
          history.replace(`/libraries/purchase-order-template/edit/${newTemplateId}`);
        }
      }
    } catch (error) {
      toasterMsg(t('PO_TEMPLATE.SAVE_ERROR') || 'Failed to save template', 'danger');
    }
  };

  const handleRearrange = () => {
    setShowRearrangeModal(true);
  };

  const toggleRearrangeModal = () => {
    setShowRearrangeModal(false);
  };

  /**
   * Handle saving sequence after rearranging sections
   * @param {Event} e - Click event from DragDropSort
   * @param {Object} document - Document containing the sequence data
   */
  const handleSaveSequence = async (e, document) => {
    try {
      const rearrangedSections = document?.sequence || [];
      if (paramId) {
        const res = await reduxDispatch(action.rearrangePOTemplateSections(paramId, rearrangedSections, t));
        if (res?.code === 200) {
          dispatch({
            type: 'BATCH_UPDATE_SECTIONS',
            sections: rearrangedSections,
          });
        }
      } else {
        dispatch({
          type: 'BATCH_UPDATE_SECTIONS',
          sections: rearrangedSections,
        });
      }
      setShowRearrangeModal(false);
    } catch (error) {
      toasterMsg(t('PO_TEMPLATE.REARRANGE_ERROR') || 'Failed to rearrange sections', 'danger');
    }
  };

  const getSectionTitle = (section) => {
    switch (section?.sectionKey) {
      case 'shipping_details_section':
        return t('PO_TEMPLATE.SECTIONS.SHIPPING_DETAILS');
      case 'order_details_section':
        return t('PO_TEMPLATE.SECTIONS.ORDER_DETAILS');
      case 'grand_total_section':
        return t('PO_TEMPLATE.SECTIONS.GRAND_TOTAL');
      default:
        return section?.name || t('PO_TEMPLATE.SECTIONS.CUSTOM_SECTION');
    }
  };

  const prepareSectionsForRearrange = () => {
    const sections = detailState?.sections || [];
    const sortedSections = [...sections].sort((a, b) => a.sequence - b.sequence);

    return sortedSections.map((section) => ({
      ...section,
      title: getSectionTitle(section),
      _id: section.id,
    }));
  };

  return (
    <div>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title w-xs-300px">{t('PO_TEMPLATE.TITLE')}</h4>
        </div>
        <button type="button" className="btn" aria-label={t('COMMON_LABEL.BACK')} onClick={goBackToListingPage}>
          <i className="icon-close cursor-pointer" />
        </button>
      </div>
      <div className="floting-actions">
        <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
          <button aria-label="save" type="button" onClick={handleSave} className="btn" disabled={detailState?.isLoading}>
            <i className="icon-Save-1" />
          </button>
        </CustomTooltip>
        <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.REARRANGE')}>
          <button aria-label="rearrange" type="button" onClick={handleRearrange} className="btn" disabled={detailState?.isLoading}>
            <i className="icon-Rearrange" />
          </button>
        </CustomTooltip>
      </div>
      {detailState?.isLoading ? (
        <Loader />
      ) : (
        <Container className="p-0 add-edit-container PO-template-container " fluid>
          <div className="potemplate-builder-container">
            <LeftPanel />
            <RightPanel />
          </div>

          <DragDropSort
            isShow={showRearrangeModal}
            isToggle={toggleRearrangeModal}
            itemList={prepareSectionsForRearrange()}
            saveSequence={handleSaveSequence}
            fromComp="POTemplate"
          />
        </Container>
      )}
    </div>
  );
}

export default function PurchaseOrderTemplateBuilder() {
  return (
    <POTemplateDndProvider>
      <PurchaseOrderTemplateContent />
    </POTemplateDndProvider>
  );
}
